import { FileCode2 } from "lucide-react";
import { useTheme } from "next-themes";
import { DiffCodeEditor } from "@ncoder/code-editor";
import type { GitCommit as GitCommitType, GitPrFile } from "@/services/git/types";
import { PaneEmpty } from "./shared";
import { parsePatch, detectLanguage, statusLabel } from "./helpers";

// ─── Diff Pane ────────────────────────────────────────────────────────────────

export function DiffPane({
  files, selectedFile, onSelectFile,
}: {
  files: GitPrFile[];
  selectedFile: GitPrFile | null;
  onSelectFile: (f: GitPrFile) => void;
}) {
  const { resolvedTheme } = useTheme();
  const monacoTheme = resolvedTheme === "dark" ? "vs-dark" : "vs";

  if (files.length === 0) return <PaneEmpty text="No file changes." />;

  const diff = selectedFile ? parsePatch(selectedFile.patch) : null;
  const language = selectedFile ? detectLanguage(selectedFile.filename) : "plaintext";

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* File list sidebar */}
      <div className="flex w-52 shrink-0 flex-col overflow-y-auto border-r border-border">
        <div className="sticky top-0 z-10 border-b border-border bg-card px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {files.length} file{files.length !== 1 ? "s" : ""}
        </div>
        {files.map((file) => {
          const { letter, color } = statusLabel(file.status);
          const isActive = selectedFile?.filename === file.filename;
          return (
            <button
              key={file.filename}
              type="button"
              onClick={() => onSelectFile(file)}
              className={[
                "flex w-full items-start gap-2 border-b border-border/40 px-3 py-2 text-left transition-colors last:border-0",
                isActive ? "bg-primary/10" : "hover:bg-muted/30",
              ].join(" ")}
            >
              <span className={`mt-0.5 shrink-0 rounded px-1 font-mono text-[9px] font-bold ${color}`}>
                {letter}
              </span>
              <div className="min-w-0">
                <p className="truncate font-mono text-[11px] leading-snug">
                  {file.filename.split("/").pop()}
                </p>
                <p className="truncate font-mono text-[9px] text-muted-foreground">
                  {file.filename.includes("/") ? file.filename.split("/").slice(0, -1).join("/") : ""}
                </p>
                <p className="mt-0.5 font-mono text-[9px]">
                  <span className="text-green-500">+{file.additions}</span>
                  {" "}
                  <span className="text-red-400">−{file.deletions}</span>
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Monaco diff editor */}
      <div className="flex flex-1 min-w-0 flex-col overflow-hidden">
        {selectedFile && (
          <div className="flex items-center justify-between border-b border-border bg-muted/10 px-4 py-1.5">
            <div className="flex items-center gap-2">
              <FileCode2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-mono text-xs text-muted-foreground">{selectedFile.filename}</span>
            </div>
            <div className="flex gap-3 font-mono text-[10px]">
              <span className="text-green-500">+{selectedFile.additions}</span>
              <span className="text-red-400">−{selectedFile.deletions}</span>
            </div>
          </div>
        )}
        <div className="flex-1 min-h-0">
          {!selectedFile ? (
            <PaneEmpty text="Select a file to view the diff." />
          ) : !diff ? (
            <PaneEmpty text="No diff available for this file." />
          ) : (
            <DiffCodeEditor
              original={diff.original}
              modified={diff.modified}
              language={language}
              theme={monacoTheme}
              viewMode="side-by-side"
              readOnly
              className="h-full"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PR Commits Tab ───────────────────────────────────────────────────────────

export function PrCommitsTab({ commits }: { commits: GitCommitType[] }) {
  if (commits.length === 0) return <PaneEmpty text="No commits found." />;
  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="space-y-1.5">
        {commits.map((c) => (
          <a
            key={c.sha}
            href={c.html_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-start justify-between rounded-md border border-border/60 bg-muted/20 px-3 py-2.5 transition-colors hover:bg-muted/40"
          >
            <div className="min-w-0">
              <p className="truncate text-sm">{c.message.split("\n")[0]}</p>
              <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                {c.author_name}
                {c.committed_at && <> · {new Date(c.committed_at).toLocaleDateString()}</>}
              </p>
            </div>
            <span className="ml-3 shrink-0 font-mono text-[10px] text-muted-foreground">{c.sha.slice(0, 7)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}
